// Core building blocks: localStorage auth + referral, the fetch wrapper,
// and the SSE idle-timeout helper. Endpoint modules build on these.

import type { ReferralSource, UserMe } from './types';

const TOKEN_KEY = 'soul_tarot_token';
const USER_KEY = 'soul_tarot_user';
// RC-P2 (STA-127) — referral payload captured from a `?ref=CODE` URL
// param before login. We don't auto-redeem on landing — the onboarding
// step 3 form pre-fills it and the user has to confirm.
const PENDING_REFERRAL_CODE_KEY = 'soul_tarot_pending_ref';
const PENDING_REFERRAL_SOURCE_KEY = 'soul_tarot_pending_ref_source';

export const Auth = {
  getToken(): string | null {
    return localStorage.getItem(TOKEN_KEY);
  },
  setToken(t: string) {
    localStorage.setItem(TOKEN_KEY, t);
  },
  getUser(): UserMe | null {
    const raw = localStorage.getItem(USER_KEY);
    if (!raw) return null;
    try {
      return JSON.parse(raw) as UserMe;
    } catch {
      return null;
    }
  },
  setUser(u: UserMe) {
    localStorage.setItem(USER_KEY, JSON.stringify(u));
  },
  clear() {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    // Note: pending referral code is intentionally NOT cleared on
    // logout so an anonymous → login → logout → re-login round trip
    // still preserves the original referral attribution.
  },
};

export const PendingReferral = {
  capture(code: string, source: 'url_param_in_onboarding' = 'url_param_in_onboarding') {
    // Server validates format on redeem; here we just upper-case.
    localStorage.setItem(PENDING_REFERRAL_CODE_KEY, code.trim().toUpperCase());
    localStorage.setItem(PENDING_REFERRAL_SOURCE_KEY, source);
  },
  get(): { code: string; source: ReferralSource } | null {
    const code = localStorage.getItem(PENDING_REFERRAL_CODE_KEY);
    const source = localStorage.getItem(PENDING_REFERRAL_SOURCE_KEY);
    if (!code) return null;
    return {
      code,
      source: (source as ReferralSource) || 'url_param_in_onboarding',
    };
  },
  clear() {
    localStorage.removeItem(PENDING_REFERRAL_CODE_KEY);
    localStorage.removeItem(PENDING_REFERRAL_SOURCE_KEY);
  },
};

/**
 * Read `?ref=CODE` from the URL on app boot and persist to localStorage.
 * Idempotent — call from App.tsx mount.
 *
 * m5 — server validates format on redeem, but reject obviously-malformed
 * codes here too so we don't carry around garbage in localStorage that
 * the onboarding form will pre-fill and then fail on. Same character
 * set the server enforces: ``[A-Z0-9]``.
 */
const REFERRAL_CODE_RE = /^[A-Z0-9]+$/;

export function captureReferralFromURL() {
  try {
    const url = new URL(window.location.href);
    const ref = url.searchParams.get('ref');
    if (ref) {
      const normalized = ref.trim().toUpperCase();
      if (
        normalized.length >= 6 &&
        normalized.length <= 16 &&
        REFERRAL_CODE_RE.test(normalized)
      ) {
        PendingReferral.capture(normalized);
      }
      // Always strip ?ref= from the visible URL — even when it failed
      // validation — so refreshes don't keep retrying the bad value.
      url.searchParams.delete('ref');
      window.history.replaceState(
        {},
        document.title,
        url.pathname + (url.search ? url.search : '') + url.hash,
      );
    }
  } catch {
    // best-effort
  }
}

export class ApiError extends Error {
  constructor(
    public status: number,
    public detail: unknown,
    message: string,
  ) {
    super(message);
  }
}

/**
 * Routes that should NOT trigger an auto-redirect on 401. /login is the
 * destination, /auth/callback is mid-flight and re-redirecting back to
 * /login would lose the OAuth fragment, and / is public so a 401 there
 * usually means a transient auth check (e.g. the nav's omenToday probe)
 * rather than a stale session worth interrupting the page for.
 */
const AUTH_REDIRECT_BLOCKLIST = new Set(['/login', '/auth/callback', '/']);

/**
 * Central 401 handler for authed requests. A 401 means the token was
 * rejected (expired, revoked, or invalidated server-side); without this
 * every page would individually surface "失敗（401）" while the user
 * stays "logged in" on the client. Clears local auth and bounces to
 * /login — skips the redirect on auth-related routes to avoid loops.
 *
 * Returns true when the response was an authed 401 so callers can short
 * out before parsing the body (the page is about to navigate anyway).
 */
export function handleUnauthorized(status: number, authed: boolean): boolean {
  if (status !== 401 || !authed) return false;
  if (!Auth.getToken()) return false;
  Auth.clear();
  const here = window.location.pathname;
  if (!AUTH_REDIRECT_BLOCKLIST.has(here)) {
    window.location.href = '/login?session_expired=1';
  }
  return true;
}

/**
 * Default request timeout for the JSON call() wrapper. SSE streams
 * bypass this — they use makeSSEAbort's idle timer instead — so
 * long-running streams aren't cut off after 30s.
 */
const REQUEST_TIMEOUT_MS = 30_000;

export async function call<T>(
  path: string,
  init: RequestInit & { auth?: boolean } = {},
): Promise<T> {
  const headers = new Headers(init.headers);
  const authed = init.auth !== false;
  if (authed) {
    const t = Auth.getToken();
    if (t) headers.set('Authorization', `Bearer ${t}`);
  }
  if (init.body && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }
  // m7 — guard against stuck requests (server hung, network blackholed,
  // tab woken from sleep mid-flight). Without this every page that
  // awaited a fetch would spin its loading state forever. Compose with
  // any caller-supplied signal so external aborts still work.
  const timeoutCtrl = new AbortController();
  const timeoutId = setTimeout(
    () => timeoutCtrl.abort(),
    REQUEST_TIMEOUT_MS,
  );
  if (init.signal) {
    if (init.signal.aborted) timeoutCtrl.abort();
    else init.signal.addEventListener('abort', () => timeoutCtrl.abort(), { once: true });
  }
  let res: Response;
  try {
    res = await fetch(`/api/v1${path}`, {
      ...init,
      headers,
      signal: timeoutCtrl.signal,
    });
  } catch (e) {
    // AbortError from our timeout vs. caller cancel: if the caller's
    // signal aborted first, propagate that; otherwise it's a timeout.
    if (e instanceof DOMException && e.name === 'AbortError') {
      if (init.signal?.aborted) throw e;
      throw new ApiError(
        408,
        { detail: { code: 'TIMEOUT', message: '請求逾時，請稍後再試。' } },
        `408 Request Timeout (${REQUEST_TIMEOUT_MS / 1000}s)`,
      );
    }
    throw e;
  } finally {
    clearTimeout(timeoutId);
  }
  handleUnauthorized(res.status, authed);
  if (res.status === 204) return undefined as unknown as T;
  if (!res.ok) {
    let detail: unknown;
    try {
      detail = await res.json();
    } catch {
      detail = await res.text();
    }
    throw new ApiError(res.status, detail, `${res.status} ${res.statusText}`);
  }
  return (await res.json()) as T;
}

/**
 * Wrap an SSE stream with an idle-timeout AbortController so a hung
 * connection (server stalled, network blackholed, browser tab woken
 * out of sleep) eventually surfaces an error instead of spinning
 * forever. Caller passes ``signal`` to fetch, then calls ``bump()``
 * after every chunk to reset the idle timer. ``cancel()`` aborts the
 * controller — wire to unmount cleanup so a navigating user doesn't
 * leak the connection.
 *
 * After abort, ``reason()`` returns the Error we synthesized when the
 * idle timer fired, or null if the caller cancelled manually. Use it
 * to distinguish "real timeout" from "user navigated away" in the
 * catch block.
 */
export function makeSSEAbort(idleMs: number = 60_000): {
  signal: AbortSignal;
  bump: () => void;
  cancel: () => void;
  reason: () => Error | null;
} {
  const controller = new AbortController();
  let timer: ReturnType<typeof setTimeout> | null = null;
  let timeoutReason: Error | null = null;
  const bump = () => {
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => {
      timeoutReason = new Error(
        `SSE 連線逾時（${Math.round(idleMs / 1000)} 秒無回應）`,
      );
      controller.abort();
    }, idleMs);
  };
  const cancel = () => {
    if (timer) {
      clearTimeout(timer);
      timer = null;
    }
    if (!controller.signal.aborted) controller.abort();
  };
  return {
    signal: controller.signal,
    bump,
    cancel,
    reason: () => timeoutReason,
  };
}

/**
 * Format a thrown error for the user.
 *
 * Backend error envelopes are inconsistent — different endpoints return
 * one of:
 *   - {"detail": "plain string"}                   (FastAPI HTTPException(detail=str))
 *   - {"detail": {"code": "...", "message": "..."}}
 *   - {"error": {"code": "...", "message": "..."}}
 *
 * Until the backend converges on one shape, this helper probes all
 * three. Falls back to ``${fallback}（${status}）`` when none match,
 * so users see something actionable instead of "Error: [object Object]"
 * (which was the previous setError(String(e)) behaviour).
 *
 * Non-ApiError throwables (network failure, parse errors) get the
 * fallback alone — no status code to expose.
 */
export function formatApiError(e: unknown, fallback: string): string {
  if (e instanceof ApiError) {
    const d = e.detail;
    if (typeof d === 'string' && d.trim()) return d;
    if (d && typeof d === 'object') {
      const obj = d as {
        detail?: unknown;
        error?: { message?: unknown };
        message?: unknown;
      };
      // {"detail": {"message": "..."}} — most reading/billing endpoints
      if (obj.detail && typeof obj.detail === 'object') {
        const inner = (obj.detail as { message?: unknown }).message;
        if (typeof inner === 'string' && inner.trim()) return inner;
      }
      // {"detail": "..."} (plain string under detail)
      if (typeof obj.detail === 'string' && obj.detail.trim()) {
        return obj.detail;
      }
      // {"error": {"message": "..."}} — referral / a few others
      if (obj.error && typeof obj.error === 'object') {
        const m = obj.error.message;
        if (typeof m === 'string' && m.trim()) return m;
      }
      // {"message": "..."} — bare envelope
      if (typeof obj.message === 'string' && obj.message.trim()) {
        return obj.message;
      }
    }
    return `${fallback}（${e.status}）`;
  }
  return fallback;
}

/** Convenience helper for raw fetch sites (SSE streams) that need
 *  the Authorization header without going through `call`. */
export function authHeaders(): Record<string, string> {
  const t = Auth.getToken();
  return t ? { Authorization: `Bearer ${t}` } : {};
}
